<template>
  <div class="sidebar-content column full-height">
    <!-- Close button -->
    <div class="row justify-end q-pa-sm">
      <q-btn v-if="$q.screen.lt.md"  
        flat 
        round 
        icon="close" 
        size="sm"
        @click="$emit('close')"
        class="text-grey-6"
      />
    </div>

    <WelcomeMsg 
      v-if="$q.screen.gt.md" 
      :first-name="user.first_name"
      subtitle-class="text-body2"
      wrapper-class="q-px-md q-pt-lg q-pb-md"
      title-class="q-mb-xs"
    />

    <q-separator v-if="$q.screen.gt.md" class="q-my-sm"/>

    <!-- Search bar -->
    <div class="q-px-md q-pb-md">
      <q-input
        v-model="searchQuery"
        :placeholder="$t('Search') + '...'"
        rounded
        outlined
        dense
        class="search-input"
      >
        <template v-slot:prepend>
          <q-icon name="search" color="grey-6" />
        </template>
      </q-input>
    </div>

    <!-- No results message -->
    <EmptyState 
      v-if="searchQuery && filteredMenuItems.length === 0"
      icon="search_off"
      :title="$t('Try a different search term')"
      :description='$t("No results found for") + ` "${searchQuery}"`'
    />


    <!-- Menu items -->
    <div class="col q-px-md">
      <q-list class="menu-list">
        <q-item 
          v-for="item in filteredMenuItems" 
          :key="item.id"
          clickable 
          v-ripple
          @click="handleMenuClick(item.id)"
          class="menu-item rounded-borders"
        >
          <q-item-section avatar>
            <q-icon :name="item.icon" color="grey-6" size="1.25rem" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{ $t(item.label) }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </div>

    <!-- Bottom section -->
    <div class="q-pa-md">
      <q-list class="menu-list">
      <!-- Settings -->
      <q-item 
        clickable 
        v-ripple
        @click="handleMiscClick('settings')"
        class="menu-item rounded-borders q-mb-md"
      >
        <q-item-section avatar>
          <q-icon name="settings" color="grey-6" size="20px" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8 text-weight-medium">
            {{$t('Settings')}}
          </q-item-label>
        </q-item-section>
      </q-item>

      <!-- About -->
      <q-item 
        clickable 
        v-ripple
        @click="handleMiscClick('about')"
        class="menu-item rounded-borders q-mb-md"
      >
        <q-item-section avatar>
          <q-icon name="help_outline" color="grey-6" size="20px" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8 text-weight-medium">
            {{$t('About')}}
          </q-item-label>
        </q-item-section>
      </q-item>
      </q-list>

      <!-- Separator line -->
      <q-separator class="q-my-md" />

      <!-- Guest mode section -->
      <div v-if="!isLoggedIn" class="guest-section">
        <div class="text-grey-8 text-weight-medium q-mb-sm">
          {{$t("You're on Guest mode")}}
        </div>
        <div class="row items-center q-gutter-sm">
          <q-btn
            flat
            no-caps
            :label="$t('Register now')"
            color="grey-6"
            size="md"
            @click="handleRegister"
            class="register-link"
          />
          <q-btn
            unelevated
            no-caps
            :label="$t('SIGN IN')"
            color="positive"
            size="md"
            @click="handleSignIn"
            class="signin-btn"
          />
        </div>
      </div>

      <!-- User profile section -->
      <div v-else class="user-profile-section">
        <q-item 
          clickable 
          v-ripple
          @click="handleUserProfile"
          class="user-profile-item rounded-borders"
        >
          <q-item-section avatar>
            <q-avatar size="2.5rem" color="grey-5" class="relative">
              <q-img 
                v-if="user.avatar" 
                :src="user.avatar" 
                fit="cover"
              />
              <q-icon 
                v-else 
                name="person" 
                color="white" 
                size="20px"
              />
              
            <!-- Online/Offline Status Badge -->
            <q-badge 
              floating 
              :color="userStatus.color"
              rounded 
              style="top: -2px; right: -2px; width: 16px; height: 16px; min-width: auto;"
              :class="userStatus.class"
            >
              <q-tooltip v-if="userStatus.tooltip" :delay="500">
                {{ userStatus.tooltip }}
              </q-tooltip>
            </q-badge>
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{ user.first_name }}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{ user.email }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </div>
    </div>
  </div>

  <About
    @back="closeOverlays"
    :visible="showAbout"
  />
  <Settings
    @back="closeOverlays"
    :visible="showSettings"
  />
  <Account @back="closeOverlays" :visible="showAccount" />
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useNavigation } from '@/common/composables/useNavigation.js'
import About from '@/common/components/app/about.vue'
import Settings from '@/common/components/app/settings.vue'
import Account from '@/common/components/app/account.vue'
import EmptyState from '@/common/components/ui/empty-state.vue'
import WelcomeMsg from './welcome-msg.vue'

const { t } = useI18n({ useScope: 'global' })
// ovo dolje isto moze se koristiti s proxy.$t
// const { proxy } = getCurrentInstance()


// Props
const props = defineProps({
  isLoggedIn: {
    type: Boolean,
    default: false
  },
  user: {
    type: Object,
    default: () => ({
      name: 'Account Name',
      email: 'email@domain.com',
      avatar: null,
      first_name: 'Guest'
    })
  }
})

const $q = useQuasar()

//state
const showAbout = ref(false)
const showSettings = ref(false)
const showAccount = ref(false)

// Online/Offline tracking
const isOnline = ref(navigator.onLine)
const isActive = ref(true)
const lastSeen = ref(new Date())

// Computed user status
const userStatus = computed(() => {
  if (!props.isLoggedIn) {
    return {
      color: 'grey-4',
      class: 'status-offline',
      text: t('Guest'),
      tooltip: t('Sign in to see online status')
    }
  }

  if (isOnline.value && isActive.value) {
    return {
      color: 'positive',
      class: 'status-online',
      text: 'Online',
      tooltip: 'Active now'
    }
  } else if (isOnline.value && !isActive.value) {
    return {
      color: 'warning',
      class: 'status-away',
      text: 'Away',
      tooltip: 'Away from keyboard'
    }
  } else {
    return {
      color: 'grey-5',
      class: 'status-offline',
      text: 'Offline',
      tooltip: `Last seen ...`
    }
  }
});

// Emits
const emit = defineEmits(['close', 'menuClick', 'register', 'signIn', 'userProfile'])

// Data
const searchQuery = ref('')

// Debug - ukloniti kasnije
watch(searchQuery, (newVal) => {
  console.log('Search query changed:', newVal)
})

const { publicMenuItems, privateMenuItems } = useNavigation()

// Computed menu items based on login status
const currentMenuItems = computed(() => {
  return props.isLoggedIn ? privateMenuItems.value : publicMenuItems.value;
})

// Filtered menu items based on search
const filteredMenuItems = computed(() => {
  if (!searchQuery.value) {
    return currentMenuItems.value
  }
  
  return currentMenuItems.value.filter(item => 
    t(item.label).toLowerCase().includes(searchQuery.value.trim().toLowerCase())
  )
})

// Methods
const handleMenuClick = (itemId) => {
  emit('menuClick', itemId)
}

const handleRegister = () => {
  emit('register')
}

const handleSignIn = () => {
  emit('signIn')
}

const handleUserProfile = () => {
  showAccount.value = true;
  emit('userProfile')
}

const closeOverlays = () => {
  showAbout.value = false
  showSettings.value = false
  showAccount.value = false
}

const handleMiscClick = (item) => {
  if(item == 'about'){
    showAbout.value = true;
  } else if(item == 'settings'){
    showSettings.value = true;
  }
}
</script>

<style lang="scss" scoped>
.welcome-logo {
  width: 3.5rem;
  height: 3.5rem;
  object-fit: contain;
  flex-shrink: 0;
  margin-left: -0.625rem;
}

.text-grey-8 {
  body.body--dark & {
    color: white !important;
  }
}

.sidebar-content {
  background: white;

  body.body--dark & {
    background: $dark;
  }

  .search-input {
    :deep(.q-field__control) {
      border-radius: var(--radius-xl);
    }
  }

  .menu-list {
    .menu-item {
      margin-bottom: var(--radius-sm);

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);

        body.body--dark & {
          background-color: rgba(255, 255, 255, 0.04);
        }
      }
    }
  }

  .guest-section {
    font-size: 0.875rem;

    .register-link {
      text-decoration: underline;
      padding: var(--radius-sm) var(--gap-sm);
      min-height: auto;
    }

    .signin-btn {
      border-radius: var(--radius-xl);
      padding: 0.375rem var(--gap-md);
      min-height: auto;
      font-weight: 600;
    }
  }

  .user-profile-section {
    .user-profile-item {
      padding: var(--gap-sm) 0.75rem;
      margin: 0;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);

        body.body--dark & {
          background-color: rgba(255, 255, 255, 0.08);
        }
      }
    }
  }
}

// Desktop specific styling
@media (min-width: 48rem) {
  .sidebar-content {

    .menu-list {
      .menu-item {
        margin-bottom: 0.375rem;
        padding: 0.75rem var(--gap-md);
        border-radius: var(--radius-lg);

        .q-item__section--avatar {
          min-width: 2.5rem;
        }
      }
    }

    .user-profile-section {
      .user-profile-item {
        padding: 0.75rem var(--gap-md);
        border-radius: var(--radius-lg);
      }
    }

    .q-pa-md {
      padding: 1.25rem !important;
    }
  }
}
</style>